/*
 * What the account pages say, decided apart from the screens (docs/2026-10-19-account-email-and-auth-flows-plan.md,
 * F3, F6, F8 and section 9): one answer of a password or invitation route, for the person. Pure: the
 * caller makes the request and passes its status and body here.
 */

/** Better Auth's limits as configured in `src/auth.ts`; the invitation accept route checks the same. */
export const PASSWORD_MIN = 14;
const PASSWORD_MAX = 128;

/** Why a new password cannot be sent yet, or null when it can. Checked before the request. */
export function passwordProblem(password: string, confirm: string): string | null {
  if (password.length < PASSWORD_MIN)
    return `Use at least ${PASSWORD_MIN} characters. A short sentence is easiest to remember.`;
  if (password.length > PASSWORD_MAX) return `Use at most ${PASSWORD_MAX} characters.`;
  if (password !== confirm) return "The two passwords are not the same.";
  return null;
}

/** How long a limited route asked to wait; a wait it did not name is the shortest window, a minute. */
function retryWaitMs(retryAfter: string | null): number {
  const seconds = retryAfter === null ? Number.NaN : Number(retryAfter);
  return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 60_000;
}

type Unreachable = { readonly kind: "unreachable" };
type Limited = { readonly kind: "limited"; readonly retryAfterMs: number };

/** 0 is the network failing; a 5xx is the server failing. Both are worth trying again. */
const failed = (status: number): boolean => status === 0 || status >= 500;

export type ForgotOutcome =
  | { readonly kind: "sent" }
  | { readonly kind: "invalid" }
  | Limited
  | Unreachable;

/**
 * `POST /api/auth/request-password-reset`. Better Auth answers 200 whether or not the address has an
 * account, so "sent" never says which addresses exist; only a malformed address is a 400.
 */
export function forgotOutcome(status: number, retryAfter: string | null): ForgotOutcome {
  if (status >= 200 && status < 300) return { kind: "sent" };
  if (status === 429) return { kind: "limited", retryAfterMs: retryWaitMs(retryAfter) };
  if (failed(status)) return { kind: "unreachable" };
  return { kind: "invalid" };
}

export type ResetOutcome =
  | { readonly kind: "changed" }
  | { readonly kind: "link_dead" }
  | Limited
  | Unreachable;

/**
 * `POST /api/auth/reset-password`. The page checks the length first, so a 400 is the token: expired,
 * already used, or never issued. Better Auth does not say which, and the person's next step is the same.
 */
export function resetOutcome(status: number, retryAfter: string | null): ResetOutcome {
  if (status >= 200 && status < 300) return { kind: "changed" };
  if (status === 429) return { kind: "limited", retryAfterMs: retryWaitMs(retryAfter) };
  if (failed(status)) return { kind: "unreachable" };
  return { kind: "link_dead" };
}

export type ChangeOutcome =
  | { readonly kind: "changed" }
  | { readonly kind: "wrong_current" }
  | { readonly kind: "signed_out" }
  | Limited
  | Unreachable;

/** `POST /api/auth/change-password`: a 400 is the current password, a 401 a session that ended. */
export function changeOutcome(status: number, retryAfter: string | null): ChangeOutcome {
  if (status >= 200 && status < 300) return { kind: "changed" };
  if (status === 401) return { kind: "signed_out" };
  if (status === 429) return { kind: "limited", retryAfterMs: retryWaitMs(retryAfter) };
  if (failed(status)) return { kind: "unreachable" };
  return { kind: "wrong_current" };
}

export const LIMITED = "Too many tries. Wait a minute, then try again.";
export const UNREACHABLE = "Could not reach LeadCue. Check your connection and try again.";

/**
 * Why an invitation link cannot be used, as the accept page heads it. Each refusal code of
 * `src/invitations.ts` that a link can meet before or during acceptance has its own words; the rest,
 * and a code this page has not heard of, read as a link that does not work.
 */
export type LinkProblem = { readonly title: string; readonly detail: string };

const LINK_PROBLEMS: Readonly<Record<string, LinkProblem>> = {
  invitation_expired: {
    title: "This invitation has expired",
    detail: "Invitations work for 7 days. Ask the person who invited you to send it again.",
  },
  invitation_withdrawn: {
    title: "This invitation was withdrawn",
    detail: "The person who sent it took it back. Ask them if you still need access.",
  },
  invitation_already_accepted: {
    title: "You already have access",
    detail: "This invitation was accepted. Sign in to open LeadCue.",
  },
  invitation_workspace_unavailable: {
    title: "This invitation can no longer be used",
    detail: "A workspace it named is no longer handled in LeadCue. Ask for a new invitation.",
  },
  invitation_another_agency: {
    title: "This address belongs to another agency",
    detail: "One email address can be in one agency. Ask for an invitation to a different address.",
  },
  feature_not_available: {
    title: "Invitations are not open yet",
    detail: "Invitations to this agency are not switched on yet. Ask the person who invited you.",
  },
  invitation_rate_limited: { title: "Too many tries", detail: LIMITED },
};

const LINK_INVALID: LinkProblem = {
  title: "This link does not work",
  detail: "It may be incomplete. Open it again from the email, or ask for a new invitation.",
};

export function linkProblem(code: string | null): LinkProblem {
  return (
    (code !== null && Object.hasOwn(LINK_PROBLEMS, code) ? LINK_PROBLEMS[code] : undefined) ??
    LINK_INVALID
  );
}

/** Codes that end the accept page's form and replace it with `linkProblem`. */
export function endsTheLink(code: string | null): boolean {
  return (
    code === null ||
    Object.hasOwn(LINK_PROBLEMS, code) ||
    code === "invitation_link_invalid" ||
    code === "invitation_not_found"
  );
}

/** What the accept form says under its button for a refusal that leaves the link usable. */
export function acceptFailure(code: string | null): string {
  switch (code) {
    case "invitation_password_length":
      return `Use ${PASSWORD_MIN} to ${PASSWORD_MAX} characters.`;
    case "invitation_name_required":
      return "Add your name. It is how your colleagues see you.";
    case "invitation_partial_failure_sign_in":
      return "Your account was made, but joining did not finish. Sign in below to finish joining.";
    case "invitation_address_mismatch":
      return "You are signed in as someone else. Sign out, then open this link again.";
    case "invitation_sign_in_required":
      return "This address already has an account. Sign in to accept.";
    default:
      return "Joining did not finish. Nothing changed. Try again in a moment.";
  }
}

/**
 * What People says when the invite form is refused, by the code of `POST /api/work/invitations`.
 * `field` names where the sentence sits: under the address, the workspaces, or the button.
 */
export type InviteRefusal = {
  readonly field: "email" | "workspaces" | "form";
  readonly message: string;
};

export function inviteRefusal(code: string | null): InviteRefusal {
  switch (code) {
    case "invitation_already_pending_resend":
      return {
        field: "email",
        message: "This address already has an invitation waiting. Resend it from the list below.",
      };
    case "invitation_already_member_add_workspaces":
      return { field: "email", message: "This person is already in your agency." };
    case "invitation_another_agency":
      return {
        field: "email",
        message: "This address belongs to another agency. One address can be in one agency.",
      };
    case "invitation_workspace_unavailable":
      return {
        field: "workspaces",
        message:
          "A workspace you chose is no longer handled here. Reload the page and choose again.",
      };
    case "invitation_rate_limited":
      return {
        field: "form",
        message: "You have sent 10 invitations in the last hour. Wait, then try again.",
      };
    case "invalid_request":
      return { field: "email", message: "Check the email address." };
    case "agency_admin_required":
    case "feature_not_available":
      return { field: "form", message: "Only agency owners and admins can invite people here." };
    default:
      return {
        field: "form",
        message: "The invitation was not made. Nothing was sent. Try again in a moment.",
      };
  }
}

/**
 * The roles an invitation can carry, in the order the invite form offers them. A list rather than a
 * yes/no: workspace admins are in v1 (Dawid, Telegram about 00:59 CEST 2026-10-20), and that role joins
 * here once the server accepts it, without the form changing shape.
 */
export const INVITE_ROLES = [
  { value: "member", label: "Member", detail: "Opens only the workspaces chosen here." },
  {
    value: "admin",
    label: "Agency admin",
    detail: "Opens every workspace and can invite and remove people.",
  },
] as const;
export type InviteRole = (typeof INVITE_ROLES)[number]["value"];

/** A role's name for people, for invitations and for people alike. */
export function roleLabel(role: string): string {
  if (role === "owner") return "Owner";
  return INVITE_ROLES.find((choice) => choice.value === role)?.label ?? role;
}

/**
 * What an invitation's email outcome means for the person who made it. `sent` with a `capture:` id is
 * a server that keeps mail instead of sending it (`LEADCUE_REPLY_ACCOUNT_MAIL=capture`, every server
 * until production email is set up), so the link has to be passed on by hand.
 */
export type Delivery =
  | { readonly status: "sent"; readonly providerId: string }
  | { readonly status: "refused"; readonly reason: string }
  | { readonly status: "unknown" };

export function deliveryNote(
  email: string,
  delivery: Delivery,
): { readonly sent: boolean; readonly message: string } {
  if (delivery.status === "sent" && !delivery.providerId.startsWith("capture:"))
    return { sent: true, message: `Invitation sent to ${email}.` };
  if (delivery.status === "sent")
    return {
      sent: false,
      message: `Invitation made for ${email}. This server does not send email yet: copy the link and send it yourself.`,
    };
  return {
    sent: false,
    message: `Invitation made for ${email}, but the email may not have gone out. Copy the link and send it yourself.`,
  };
}
